import { z } from 'zod';
import { DateString, Timestamp, UserRef, Uuid } from './common.js';
import { ContentDateBasis, ExtractionStatus, ProseInputKind } from './vocab.js';

/**
 * Prose in, sorted material out — the record's one add box
 * (`POST /subjects/:id/inputs`, server-design.md §3.1/§4).
 */

/**
 * **The longest one prose note may be, on the wire**
 * (`docs/notes/2026-09-14-split-giant-intake-note.md`).
 *
 * It was the dashboard's own number and nothing else — the page offered it, and
 * the wire took whatever arrived. That was tolerable while a long note was one
 * row and one reading. It is not now: a note over 1,500 characters is **split**
 * before it is stored, so a body the wire accepts becomes a row *and a leg* per
 * thousand characters, and a hundred-kilobyte POST is two hundred legs, two
 * hundred model calls and the organization's month spent, out of one request
 * nobody looked at.
 *
 * So the number lives here, where the server reads it, and the page imports it
 * rather than restating it. Twenty thousand characters is about ten pages: past
 * that the thing being pasted is a document, which is the import's job.
 *
 * The **trim** beside it on every field below is the other half. It is what the
 * office sends anyway (every box trims before it posts) and it is how the cap is
 * measured on the page — and it closes a shape the splitter would otherwise
 * store: three thousand characters of whitespace are over the split threshold,
 * segment to nothing, and come back as one row of blank space.
 */
export const PROSE_NOTE_MAX_LENGTH = 20_000;

/**
 * **And the longest an organization's own statement may be**, which is the
 * number the note cap left behind (same note, 2026-09-14).
 *
 * The cap above rose because a splitter went in behind the boxes that use it.
 * Nothing went in behind this one. A statement — `POST /organisations` and the
 * lookup's `Add what you know` — is read **inside the request that files it**,
 * under `ORG_STATEMENT_RESTRICTION`: one claim type, two anchor kinds, `mint:
 * never`, no pieces and no reading leg. So its length is still what a single
 * reading has to survive, which is what two thousand was chosen for, and raising
 * it would walk a twenty-thousand-character paragraph straight back into the
 * 180-second timeout this whole arc closes.
 *
 * It lives here rather than in `organisations.ts` so that the two caps are read
 * side by side: what separates them is not the kind of prose but whether
 * anything cuts it up before it is read, and that is a fact about the door.
 */
export const PROSE_STATEMENT_MAX_LENGTH = 2_000;

/** A note or reference fragment on the record's Notes rail. */
export const NoteEntry = z.object({
  input_id: Uuid,
  kind: ProseInputKind,
  prose: z.string(),
  content_date: DateString.nullable(),
  content_date_basis: ContentDateBasis,
  /** `prose_input.author_user_id` is NOT NULL — only the user's name can be null. */
  author: UserRef,
  extraction_status: ExtractionStatus,
  /** How many times the note has been sent to the reader; 0 until the first attempt is queued. */
  extraction_attempt: z.number().int().nonnegative(),
  /** When the current (or last) reading attempt was queued; null when nothing was ever queued. */
  extraction_started_at: Timestamp.nullable(),
  /** When the last attempt ended (done or failed); null while pending or never attempted. */
  extraction_finished_at: Timestamp.nullable(),
  /** Why the last attempt failed, in a sentence the office can read; null unless status is `failed`. */
  extraction_reason: z.string().nullable(),
  /**
   * **The paste this row is a piece of**, or null for a note stored whole
   * (split-giant-note note, 2026-09-14). A note over 1,500 characters is cut
   * before it is stored, and the rail paints one paste as one collapsed entry
   * rather than as a column of fragments; this is what says which rows belong
   * together.
   */
  split_group_id: Uuid.nullable(),
  /** Where this piece sits in its paste, 1-based; null with `split_group_id`. */
  piece_seq: z.number().int().positive().nullable(),
  /** Kept out of research: never shipped to a run, never read into claims. */
  confidential: z.boolean(),
  /**
   * Who last rewrote the note and when, or null for words as first filed. An
   * edit keeps the note's author and `created_at`: it is still their note, and
   * a rewrite must not make an old note read as the office's newest view.
   */
  edited: z.object({ by: UserRef, at: Timestamp }).nullable(),
  created_at: Timestamp,
});
export type NoteEntry = z.infer<typeof NoteEntry>;

/** An interaction row on the timeline; its prose lives in the backing `prose_input`. */
export const InteractionEntry = z.object({
  id: Uuid,
  input_id: Uuid,
  occurred_at: Timestamp,
  prose: z.string(),
  participants: z.array(z.string()).nullable(),
  /** `interaction.recorded_by_user_id` is NOT NULL — only the user's name can be null. */
  recorded_by: UserRef,
  created_at: Timestamp,
});
export type InteractionEntry = z.infer<typeof InteractionEntry>;

/**
 * `POST /subjects/:id/interactions` — the pre-categorized shortcut
 * (server-design.md §3.5). It stays in the dashboard plane and therefore in this
 * package, though the dashboard's own Add box routes through
 * `POST /subjects/:id/inputs` instead. No extraction happens in stage 1.
 */
export const CreateInteractionBody = z
  .object({
    occurred_at: Timestamp,
    prose: z.string().min(1),
    participants: z.array(z.string()).optional(),
  })
  .strict();
export type CreateInteractionBody = z.infer<typeof CreateInteractionBody>;

export const CreateInteractionResponse = z.object({
  interaction: InteractionEntry,
});
export type CreateInteractionResponse = z.infer<typeof CreateInteractionResponse>;

/**
 * `POST /subjects/:id/inputs`. No client-supplied `kind` — the extractor
 * categorizes. `content_date` is offered, never required: a note typed today
 * about a 2019 conversation must not win on recency.
 */
export const CreateInputBody = z
  .object({
    /* Trimmed, then bounded: see {@link PROSE_NOTE_MAX_LENGTH}. The `min(1)`
       lands *after* the trim, which is what refuses a body that is only
       whitespace — the splitter would otherwise store it as one blank row. */
    prose: z.string().trim().min(1).max(PROSE_NOTE_MAX_LENGTH),
    content_date: DateString.optional(),
    /**
     * **Keep this note out of research** (ruled 2026-09-19). Stored and shown on
     * the record like any other; never shipped to a run and never read into
     * claims. Chosen here and not changed afterwards. Offered only where the
     * org's vertical says so (`verticalOffersConfidentialNotes`); the server
     * refuses it elsewhere with `VALIDATION_FAILED`.
     */
    confidential: z.boolean().optional(),
  })
  .strict();
export type CreateInputBody = z.infer<typeof CreateInputBody>;

/**
 * `PATCH /inputs/:id` — an owner rewriting a note (2026-10-29). The id may be
 * any piece of a long paste: the whole paste is replaced by these words, which
 * are stored and read exactly as the add box stores and reads a new note.
 * Author, dates and confidentiality stay the note's own.
 */
export const EditInputBody = z
  .object({
    prose: z.string().trim().min(1).max(PROSE_NOTE_MAX_LENGTH),
  })
  .strict();
export type EditInputBody = z.infer<typeof EditInputBody>;

/** `DELETE /inputs/:id` — how many rows the removed note was stored as. */
export const RemoveInputResponse = z.object({
  removed_pieces: z.number().int().positive(),
});
export type RemoveInputResponse = z.infer<typeof RemoveInputResponse>;

/**
 * The sorted result, returned directly so the Add modal renders what landed
 * without a refetch (decided 2026-08-26). `kind` on a mixed row is `note`;
 * interaction segments materialize their own rows and appear in
 * `created.interactions`.
 */
export const InputExtractionResult = z.object({
  input: z.object({
    id: Uuid,
    kind: ProseInputKind,
    extraction_status: ExtractionStatus,
  }),
  /**
   * **How many rows the prose was stored as** (split-giant-note note,
   * 2026-09-14). A note over 1,500 characters is cut before it is stored, so the
   * add box's own answer has to be able to say *saved in eight pieces* rather
   * than reporting one note and painting eight. `1` is an unsplit note, a retry
   * and a reclassify — all three are one row — and `input` is always the first
   * piece, which is the one the office's words open with.
   */
  pieces: z.number().int().positive(),
  created: z.object({
    interactions: z.array(InteractionEntry),
    /** Claims extracted from the note/reference material — a count, not a list. */
    claim_count: z.number().int().nonnegative(),
    anchor_count: z.number().int().nonnegative(),
  }),
});
export type InputExtractionResult = z.infer<typeof InputExtractionResult>;

/** `POST /inputs/:id/extract-retry` — idempotent per prose_input, same payload. */
export const ExtractRetryResponse = InputExtractionResult;
export type ExtractRetryResponse = z.infer<typeof ExtractRetryResponse>;

/**
 * `POST /interactions/:id/reclassify` — no body. Deletes the interaction
 * metadata row and re-extracts its content as note material. One direction only
 * in stage 1 (deliberate): a missed interaction is handled by re-entering that
 * line, so there is no note → interaction route.
 */
export const ReclassifyInteractionResponse = InputExtractionResult;
export type ReclassifyInteractionResponse = z.infer<typeof ReclassifyInteractionResponse>;
